import api from './api';

export interface GeoInfo {
  city: string | null;
  region: string | null;
  country: string | null;
  lat: number | null;
  lon: number | null;
}

export interface ActivityEvent {
  id: number;
  actor: string;
  category: string;
  method: string;
  path: string;
  route_pattern: string | null;
  query: Record<string, string> | null;
  ip: string | null;
  user_agent: string | null;
  geo: GeoInfo | null;
  created_at: string;
}

export interface ActivitySummaryActor {
  actor: string;
  total_requests: number;
  parcel_views: number;
  briefings: number;
  shares: number;
  county_drills: number;
  searches: number;
  first_seen: string;
  last_seen: string;
}

export interface ApiKey {
  id: number;
  actor: string;
  role: 'owner' | 'client';
  label: string | null;
  password_hint: string | null;
  created_at: string;
  revoked_at: string | null;
}

export interface CreateKeyResponse extends ApiKey {
  key: string;
}

// Zone share creator — still called from ZoneBriefing.tsx's in-app Share modal.
// Read/revoke flow moved to the unified /admin/shares endpoints below.
export interface CreateZoneShareTokenResponse {
  id: string;
  token: string;
  url: string;
  zone_slug: string;
  label: string | null;
  expires_at: string;
  created_at: string;
}

export const createZoneShareToken = async (
  zone_slug: string,
  label?: string,
  expires_in_days?: number
): Promise<CreateZoneShareTokenResponse> => {
  const res = await api.post('/admin/zone-share-tokens', { zone_slug, label, expires_in_days });
  return res.data;
};

// Portfolio share creator — called from the admin Shares tab's create form.
// Read/revoke flow moved to the unified /admin/shares endpoints below.
export interface CreatePortfolioShareTokenResponse {
  id: string;
  token: string;
  url: string;
  client_slug: string;
  label: string | null;
  expires_at: string;
  created_at: string;
}

export const createPortfolioShareToken = async (
  client_slug: string,
  label?: string,
  expires_in_days?: number
): Promise<CreatePortfolioShareTokenResponse> => {
  const res = await api.post('/admin/portfolio-share-tokens', { client_slug, label, expires_in_days });
  return res.data;
};

export const fetchActivity = async (params?: {
  actor?: string;
  from?: string;
  to?: string;
  category?: string;
  limit?: number;
  offset?: number;
}): Promise<{ events: ActivityEvent[]; total: number }> => {
  const res = await api.get('/admin/activity', { params });
  return res.data;
};

export const fetchActivitySummary = async (): Promise<{ actors: ActivitySummaryActor[] }> => {
  const res = await api.get('/admin/activity/summary');
  return res.data;
};

export const fetchApiKeys = async (): Promise<{ keys: ApiKey[] }> => {
  const res = await api.get('/admin/keys');
  return res.data;
};

export const createApiKey = async (actor: string, role: 'owner' | 'client', label?: string, password?: string): Promise<CreateKeyResponse> => {
  const res = await api.post('/admin/keys', { actor, role, label, password });
  return res.data;
};

export const revokeApiKey = async (id: number): Promise<{ success: boolean }> => {
  const res = await api.post(`/admin/keys/${id}/revoke`);
  return res.data;
};

// ── Unified shares (parcel briefing + zone + portfolio in one list) ────────

export type ShareType = 'briefing' | 'zone' | 'portfolio';

export interface UnifiedShare {
  type: ShareType;
  id: string;
  token: string;
  label: string | null;
  view_count: number;
  target_id: string;
  target_label: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  created_by: string | null;
  actor: string;
  carve_out_slug: string | null;
  profile: string | null;
  layouts: string | null;
  hit_count: number;
  unique_ips: number;
  human_ips: number;
  last_opened_at: string | null;
  top_cities: { city: string | null; region: string | null }[];
  human_city_count: number;
}

export interface ShareSession {
  ip: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  hit_count: number;
  user_agent: string | null;
  first_path: string;
  is_bot: boolean;
  geo: GeoInfo | null;
}

export const fetchUnifiedShares = async (): Promise<{ shares: UnifiedShare[] }> => {
  const res = await api.get('/admin/shares');
  return res.data;
};

export const fetchShareSessions = async (
  type: ShareType,
  id: string,
): Promise<{ actor: string; sessions: ShareSession[] }> => {
  const res = await api.get(`/admin/shares/${type}/${id}/sessions`);
  return res.data;
};

export const revokeShare = async (
  type: ShareType,
  id: string,
): Promise<{ success: boolean }> => {
  const res = await api.post(`/admin/shares/${type}/${id}/revoke`);
  return res.data;
};
